import React, { useCallback, useMemo, useState } from 'react';
import { CountryEnum } from '@/constants/country';
import { Stack, Text, useDisclosure, VStack } from '@chakra-ui/react';
import { BuildingShowcaseEntity, GetOptionsForBuildingFilter, LocationData, LocationDataEntity } from '@/swagger';
import classNames from 'classnames';
import CustomRadio from '@/src/components/UI/CustomRadio';
import { isNotEmptyArray, isString } from '@/utils/formatChecker';
import ConditionalFragment from '@/components/common/ConditionalFragment';
import { useRouter } from 'next/router';
import { useAsync } from 'react-use';
import { locationData } from '@/lib/constant';
import CityOptions, { CitySectionOptions } from '@/src/components/feature/CityOptions';
import CriteriaButton from '@/src/components/feature/CriteriaButton';

export const getCityLocationData = (state: string, city: string, location: Array<LocationData>) => {
  const theState = location.find((stateLocationData) => stateLocationData.acceptanceSearchKeywords.includes(state));
  return theState?.cities.find((cityLocationData) => cityLocationData.acceptanceSearchKeywords.includes(city));
};
export const filterShowcasesByCity = (
  showcases: Array<BuildingShowcaseEntity>,
  _cities: string[] | string,
  location: LocationDataEntity | undefined,
  country?: keyof typeof CountryEnum
) => {
  const cities = isString(_cities) ? [_cities] : _cities ?? [];
  return showcases.filter((showcase) => {
    if (country && country !== 'ALL' && showcase.country !== country) return false;
    // 只選了國家、未選任何州/城市時，視為不加城市條件，顯示該國全部建案
    if (cities.length === 0) return true;
    if (showcase.country === 'TW') {
      const replacedCities = cities.map((city) => city.replace('臺', '台'));
      return replacedCities.includes(showcase.city.replace('臺', '台'));
    } else {
      const locationData = (showcase.country === 'US' ? location?.US : location?.CA) ?? [];
      const showcaseCityLocationData = getCityLocationData(showcase.state ?? '', showcase.city ?? '', locationData);
      if (!showcaseCityLocationData) return false;
      return cities.some((city) => showcaseCityLocationData.acceptanceSearchKeywords.includes(city));
    }
  });
};

const BuildingAreaFilter: React.FC<{
  showcases: Array<BuildingShowcaseEntity>;
  areaData: GetOptionsForBuildingFilter;
}> = ({ showcases, areaData }) => {
  const [filteredCountry, setFilteredCountry] = useState<keyof typeof CountryEnum>('ALL');
  const [filteredCities, setFilteredCities] = useState<string[]>([]);
  const { value: location } = useAsync(() => locationData, []);

  const router = useRouter();
  const { cities, country } = router.query as { cities: string[]; country: keyof typeof CountryEnum };

  const { isOpen, onOpen, onClose } = useDisclosure({
    onOpen() {
      if (!country || country === 'ALL') return;
      setFilteredCountry(country);
      setFilteredCities(isString(cities) ? [cities] : cities ?? []);
    },
  });

  const areaCountryTitleString = useMemo(() => {
    if (!country) return CountryEnum.ALL;
    return CountryEnum[country];
  }, [country]);
  const areaCityTitleString = useMemo(() => {
    const _cities = isString(cities) ? [cities] : cities ?? [];
    if (_cities.length > 0) {
      return _cities.join(', ');
    } else {
      return '';
    }
  }, [cities]);

  const hasTWItem = useMemo(() => {
    return isNotEmptyArray(areaData.twCitiesData);
  }, [showcases]);
  const hasUSItem = useMemo(() => {
    return isNotEmptyArray(areaData.usCitiesData);
  }, [showcases]);
  const hasCAItem = useMemo(() => {
    return isNotEmptyArray(areaData.caCitiesData);
  }, [showcases]);

  const countryOptions = useMemo<Array<Option<keyof typeof CountryEnum>>>(() => {
    return [
      { value: 'ALL' as keyof typeof CountryEnum, label: '全部區域', active: true },
      { value: 'TW' as keyof typeof CountryEnum, label: '台灣', active: hasTWItem },
      { value: 'US' as keyof typeof CountryEnum, label: '美國', active: hasUSItem },
      { value: 'CA' as keyof typeof CountryEnum, label: '加拿大', active: hasCAItem },
    ].filter((option) => option.active);
  }, [hasTWItem, hasUSItem, hasCAItem]);

  const resultNum = useMemo(() => {
    return filteredCountry === 'ALL'
      ? showcases.length
      : filterShowcasesByCity(showcases, filteredCities, location, filteredCountry).length;
  }, [showcases, filteredCountry, filteredCities, location]);

  const onCitySelect = useCallback((city: string) => {
    setFilteredCities((prev) => (prev.includes(city) ? prev.filter((c) => c !== city) : [...prev, city]));
  }, []);
  const onCountrySelect = useCallback((country: keyof typeof CountryEnum) => {
    setFilteredCountry(country);
    setFilteredCities([]);
  }, []);
  const onReset = useCallback(() => {
    setFilteredCountry('ALL');
    setFilteredCities([]);
  }, []);

  const closeFilter = useCallback(() => {
    setFilteredCountry(country);
    setFilteredCities(isString(cities) ? [cities] : cities ?? []);
    onClose();
  }, [country, cities]);

  const onApply = useCallback(() => {
    router.replace(
      {
        pathname: router.pathname,
        query:
          filteredCountry === 'ALL'
            ? {}
            : {
                country: filteredCountry,
                cities: filteredCities,
              },
      },
      undefined,
      { shallow: true }
    );
    onClose();
  }, [filteredCountry, filteredCities, router, onClose]);

  return (
    <CriteriaButton
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={closeFilter}
      onReset={onReset}
      onApply={onApply}
      resultNum={resultNum}
      buttonText={
        <React.Fragment>
          <Text>{areaCountryTitleString}</Text>
          {areaCityTitleString != '' && (
            <>
              <Stack w={'1px'} h={'20px'} bgColor={'black'} />
              <Text overflow={'hidden'} isTruncated textOverflow={'ellipsis'} textAlign={'left'} className={'flex-1'}>
                {areaCityTitleString}
              </Text>
            </>
          )}
        </React.Fragment>
      }
    >
      <VStack w={'full'}>
        <CustomRadio
          disableDesktopExtraStyle
          activeIdx={filteredCountry}
          onChange={onCountrySelect}
          defaultIdx={0}
          options={countryOptions}
        />
      </VStack>
      <ConditionalFragment condition={filteredCountry !== 'ALL'}>
        <Stack bgColor={'gray.200'} w={'full'} h={'1px'} my={{ md: '4' }} className={'px-6 flex-shrink-0'} />
      </ConditionalFragment>
      <div className={classNames('px-4 w-screen md:w-full', 'relative flex flex-col overflow-scroll')}>
        {filteredCountry === 'TW' && (
          <CityOptions data={areaData.twCitiesData} cities={filteredCities} onCitySelect={onCitySelect} />
        )}
        {filteredCountry === 'US' && (
          <CitySectionOptions
            data={areaData.usCitiesData}
            cities={filteredCities}
            setFilteredCities={setFilteredCities}
            onCitySelect={onCitySelect}
            location={location?.US}
          />
        )}
        {filteredCountry === 'CA' && (
          <CitySectionOptions
            data={areaData.caCitiesData}
            cities={filteredCities}
            setFilteredCities={setFilteredCities}
            onCitySelect={onCitySelect}
            location={location?.CA}
          />
        )}
      </div>
    </CriteriaButton>
  );
};

export default BuildingAreaFilter;
